import React, { useState } from "react";
import Cockpit from "./Cockpit.jsx";
import TreasuryModal from "./TreasuryModal.jsx";
import { ApprovalsDrawer, BreachesDrawer } from "./ApprovalsBreaches.jsx";
import { fmtAbbr } from "../../components/terminal/format.js";
import {
  MOCK_ENTITIES, MOCK_ASSET_CLASSES, MOCK_DIGITAL_HOLDINGS, MOCK_YIELD_POSITIONS,
  MOCK_CUSTODY, MOCK_CHAINS, MOCK_SWEEP_RULES, MOCK_FX_EXPOSURE,
  MOCK_COCKPIT_STATE, MOCK_YIELD_PRODUCTS,
} from "./mockData.js";

/* Treasury — multi-entity, multi-currency, multi-chain command surface.
   Layout: cockpit chrome on top → AUM strip → three-column body
   (entities + class detail · digital holdings + yield · custody +
   chain + sweeps + FX).

   Currently rendered against MOCK data while the §D backend pillars
   stabilize. The live-data hooks in `useTerminalTreasury.js` and the
   action-dispatch logic in TerminalAPI are ready to wire back in
   once §D.1–§D.11 are confirmed stable; see BACKEND_TERMINAL_SPEC.md
   and BACKEND_TERMINAL_SPEC_V2.md. */

export default function TreasuryScreen({
  currentUserEmail,
  entities = MOCK_ENTITIES,
  assetClasses = MOCK_ASSET_CLASSES,
  digitalHoldings = MOCK_DIGITAL_HOLDINGS,
  yieldPositions = MOCK_YIELD_POSITIONS,
  custody = MOCK_CUSTODY,
  chains = MOCK_CHAINS,
  sweepRules = MOCK_SWEEP_RULES,
  fxExposure = MOCK_FX_EXPOSURE,
  cockpitState = MOCK_COCKPIT_STATE,
  yieldProducts = MOCK_YIELD_PRODUCTS,
}) {
  const [activeAction, setActiveAction] = useState(null);
  const [entityId, setEntityId] = useState(entities[0]?.id);
  const [classKey, setClassKey] = useState("STABLE");
  const [drawer, setDrawer] = useState(null);  // 'approvals' | 'breaches' | null

  /* Modal submit is a no-op in mock mode — the modal closes without
     hitting the backend so demos / mock walkthroughs don't error.
     When we re-enable live wiring this maps each payload.id to the
     matching terminalAPI mutating call (see git history for the
     prior implementation). */
  const submitAction = () => {};

  const totalAum = assetClasses.reduce((a, c) => a + (parseFloat(c.value_usd) || 0), 0);
  const klass = assetClasses.find((c) => c.key === classKey) || assetClasses[0];

  const digitalTotal = digitalHoldings.reduce((a, h) => a + (parseFloat(h.value_usd) || 0), 0);
  const yieldTotal   = yieldPositions.reduce((a, y) => a + (parseFloat(y.principal_usd) || 0), 0);
  const yieldEarned  = yieldPositions.reduce((a, y) => a + (parseFloat(y.earned_30d_usd) || 0), 0);
  const blendedApy   = yieldTotal ? yieldPositions.reduce((a, y) => a + (y.apy * (parseFloat(y.principal_usd) || 0)), 0) / yieldTotal : 0;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 40px - 30px)" }}>
      <Cockpit
        state={cockpitState}
        onAction={setActiveAction}
        onOpenApprovals={() => setDrawer("approvals")}
        onOpenBreaches={() => setDrawer("breaches")}
      />
      <TreasuryModal
        action={activeAction}
        onClose={() => setActiveAction(null)}
        onSubmit={submitAction}
        yieldProducts={yieldProducts}
      />
      {drawer === "approvals" && (
        /* Token omitted → drawer falls through to its MOCK_APPROVALS
           list. Restore `token={token}` and the API-driven onApprove
           / onReject handlers when the §D.10 endpoints stabilize. */
        <ApprovalsDrawer
          currentUserEmail={currentUserEmail}
          onClose={() => setDrawer(null)}
          onApprove={() => setDrawer(null)}
          onReject={() => setDrawer(null)}
        />
      )}
      {drawer === "breaches" && (
        <BreachesDrawer
          onClose={() => setDrawer(null)}
          onAck={() => setDrawer(null)}
          onActOn={(b) => {
            setDrawer(null);
            setActiveAction({ id:b.suggested_action.id, label:b.suggested_action.label, accent:"#FF5F6B" });
          }}
        />
      )}

      {/* AUM strip + class allocation */}
      <div style={{ borderBottom:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
        <div style={{ display:"grid", gridTemplateColumns:"auto 1fr", gap:24, padding:"14px 18px", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:9.5, letterSpacing:"0.18em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600 }}>TOTAL AUM · GROUP · USD-EQ</div>
            <div style={{ display:"flex", alignItems:"baseline", gap:14, marginTop:4 }}>
              <span className="mono" style={{ fontSize:30, color:"var(--term-ink-1)", letterSpacing:"-0.02em" }}>${(totalAum / 1_000_000).toFixed(2)}M</span>
              <span className="mono" style={{ fontSize:13, color:"var(--term-green)" }}>+0.42%</span>
              <span className="mono" style={{ fontSize:11, color:"var(--term-ink-3)" }}>24H · +$72,114</span>
              <span className="mono" style={{ fontSize:11, color:"var(--term-ink-3)" }}>·  {entities.length} entities · {digitalHoldings.length} digital · {yieldPositions.length} yield</span>
            </div>
          </div>
          <div>
            <div style={{ display:"flex", height:18, border:"1px solid var(--term-line)", overflow:"hidden" }}>
              {assetClasses.map((c, i) => (
                <div
                  key={c.key}
                  onClick={() => setClassKey(c.key)}
                  title={`${c.label} · ${fmtAbbr(c.value_usd)}`}
                  style={{
                    flex: parseFloat(c.value_usd), background: c.color_hint,
                    opacity: classKey === c.key ? 1 : 0.55,
                    borderRight: i < assetClasses.length - 1 ? "1px solid var(--term-bg)" : "none",
                    cursor:"pointer", transition:"opacity .15s",
                  }}
                />
              ))}
            </div>
            <div style={{ display:"flex", gap:14, marginTop:6, fontSize:10, color:"var(--term-ink-2)", flexWrap:"wrap" }}>
              {assetClasses.map((c) => (
                <span key={c.key} onClick={() => setClassKey(c.key)} style={{ display:"inline-flex", alignItems:"center", gap:6, cursor:"pointer", opacity: classKey === c.key ? 1 : 0.7 }}>
                  <span style={{ width:8, height:8, background:c.color_hint }} />
                  <span style={{ color:"var(--term-ink-2)" }}>{c.label}</span>
                  <span className="mono" style={{ color:"var(--term-ink-1)" }}>{((parseFloat(c.value_usd) / totalAum) * 100).toFixed(1)}%</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* three-column body */}
      <div style={{ flex:1, minHeight:0, display:"grid", gridTemplateColumns:"320px 1fr 360px" }}>
        {/* LEFT — entities + class detail */}
        <div className="term-pane">
          <div className="term-pane-head">
            Entities · group <span className="meta">{entities.length} legal · {new Set(entities.map((e) => e.jurisdiction_iso)).size} jurisdictions</span>
          </div>
          <div className="term-pane-body" style={{ flex:"none", maxHeight:"46%" }}>
            {entities.map((e) => {
              const sel = e.id === entityId;
              return (
                <div key={e.id} onClick={() => setEntityId(e.id)} style={{
                  position:"relative", padding:"11px 14px", borderBottom:"1px solid var(--term-line-dim)",
                  cursor:"pointer", background: sel ? "var(--term-hover-bg)" : "transparent",
                }}>
                  {sel && <span style={{ position:"absolute", left:0, top:0, bottom:0, width:2, background:"var(--term-green)" }} />}
                  <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:6 }}>
                    <span style={{ fontSize:12.5, color:"var(--term-ink-1)" }}>{e.name}</span>
                    <span className="mono" style={{ fontSize:9, color:"var(--term-ink-3)", letterSpacing:"0.10em" }}>{e.jurisdiction_iso}</span>
                  </div>
                  <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginTop:5 }}>
                    <span className="mono" style={{ fontSize:11, color:"var(--term-ink-1)" }}>{fmtAbbr(e.aum_usd)}</span>
                    <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)" }}>{e.share_pct.toFixed(1)}%</span>
                  </div>
                  <div style={{ position:"relative", height:3, marginTop:5, background:"var(--term-line-dim)" }}>
                    <span style={{ position:"absolute", inset:0, width:`${e.share_pct}%`, background: sel ? "var(--term-green)" : "#4A8CFF", opacity:0.7 }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="term-pane-head term-pane-split">
            {klass.label} <span className="meta">{fmtAbbr(klass.value_usd)} · {((parseFloat(klass.value_usd) / totalAum) * 100).toFixed(1)}%</span>
          </div>
          <div className="term-pane-body">
            <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--term-line-dim)" }}>
              <div style={{ fontSize:9, letterSpacing:"0.18em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:6 }}>COMPOSITION</div>
              {(klass.composition || []).map((m, i) => (
                <div key={i} className="mono" style={{ fontSize:11, color:"var(--term-ink-2)", padding:"2px 0", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ width:6, height:6, background:klass.color_hint }} />{m}
                </div>
              ))}
            </div>
            <div style={{ padding:"12px 14px", display:"flex", flexDirection:"column", gap:6 }}>
              <button className="term-util-btn">Rebalance class</button>
              <button className="term-util-btn">Set policy</button>
              <button className="term-util-btn">Export holdings</button>
            </div>
          </div>
        </div>

        {/* CENTER — digital + yield */}
        <div className="term-pane">
          <div className="term-pane-head">
            Digital asset positions
            <span className="meta"><b>{fmtAbbr(digitalTotal)}</b> · {digitalHoldings.length} assets · {new Set(digitalHoldings.map((h) => h.chain)).size} chains</span>
          </div>
          <div className="term-pane-body" style={{ flex:"none" }}>
            <div style={{ display:"grid", gridTemplateColumns:"56px 1fr 90px 96px 80px 70px 80px", gap:8, alignItems:"center", padding:"0 14px", height:22, fontSize:9.5, letterSpacing:"0.12em", color:"var(--term-ink-2)", textTransform:"uppercase", background:"var(--term-bg)", position:"sticky", top:0, zIndex:1, fontWeight:600, borderBottom:"1px solid var(--term-line-dim)" }}>
              <span>SYM</span><span>CUSTODY · CHAIN</span><span style={{ textAlign:"right" }}>QTY</span><span style={{ textAlign:"right" }}>VALUE USD</span><span>TYPE</span><span style={{ textAlign:"right" }}>24H</span><span style={{ textAlign:"right" }}>PRICE</span>
            </div>
            {digitalHoldings.map((h, i) => {
              const up = h.delta_24h_pct > 0;
              const flat = h.delta_24h_pct === 0;
              const typeColor = h.type === "COLD" ? "#06B6D4" : h.type === "STAKED" ? "#A78BFA" : "#F5A524";
              return (
                <div key={i} className="term-tx-row term-has-ctx" style={{ gridTemplateColumns:"56px 1fr 90px 96px 80px 70px 80px", position:"relative" }}>
                  <span className="mono" style={{ fontSize:12, color:"var(--term-ink-1)", fontWeight:500 }}>{h.symbol}</span>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:11.5, color:"var(--term-ink-1)" }}>{h.custody_venue}</div>
                    <div className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.04em" }}>{h.chain}</div>
                  </div>
                  <span className="mono" style={{ textAlign:"right", fontSize:11, color:"var(--term-ink-2)" }}>{Number(h.quantity).toLocaleString(undefined, { maximumFractionDigits:4 })}</span>
                  <span className="mono" style={{ textAlign:"right", fontSize:11.5, color:"var(--term-ink-1)" }}>{fmtAbbr(h.value_usd)}</span>
                  <span><span style={{ display:"inline-block", padding:"2px 6px", fontSize:9, letterSpacing:"0.10em", fontWeight:600, color:typeColor, border:`1px solid ${typeColor}33` }}>{h.type}</span></span>
                  <span className="mono" style={{ textAlign:"right", fontSize:10.5, color: flat ? "var(--term-ink-3)" : up ? "var(--term-green)" : "var(--term-red)" }}>{flat ? "0.00" : (up ? "+" : "") + h.delta_24h_pct.toFixed(2)}%</span>
                  <span className="mono" style={{ textAlign:"right", fontSize:10.5, color:"var(--term-ink-3)" }}>${Number(h.price_usd).toLocaleString()}</span>
                </div>
              );
            })}
          </div>

          <div className="term-pane-head term-pane-split">
            Yield strategies
            <span className="meta">
              <b>{fmtAbbr(yieldTotal)}</b> deployed · blended <b style={{ color:"var(--term-green)" }}>{blendedApy.toFixed(2)}%</b> APY · earned 30d <b style={{ color:"var(--term-green)" }}>+{fmtAbbr(yieldEarned)}</b>
            </span>
          </div>
          <div className="term-pane-body">
            <div style={{ display:"grid", gridTemplateColumns:"1fr 80px 100px 90px 60px", gap:8, alignItems:"center", padding:"0 14px", height:22, fontSize:9.5, letterSpacing:"0.12em", color:"var(--term-ink-2)", textTransform:"uppercase", background:"var(--term-bg)", position:"sticky", top:0, zIndex:1, fontWeight:600, borderBottom:"1px solid var(--term-line-dim)" }}>
              <span>STRATEGY</span><span>CHAIN</span><span style={{ textAlign:"right" }}>PRINCIPAL</span><span style={{ textAlign:"right" }}>30D EARNED</span><span style={{ textAlign:"right" }}>APY</span>
            </div>
            {yieldPositions.map((y, i) => {
              const riskC = y.risk === "LOW" ? "#2CCB7E" : y.risk === "MED" ? "#F5A524" : "#FF5F6B";
              return (
                <div key={i} className="term-tx-row" style={{ gridTemplateColumns:"1fr 80px 100px 90px 60px" }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:11.5, color:"var(--term-ink-1)" }}>{y.strategy_label}</div>
                    <div className="mono" style={{ fontSize:9.5, color:riskC, letterSpacing:"0.10em" }}>RISK · {y.risk}</div>
                  </div>
                  <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)", letterSpacing:"0.04em" }}>{y.chain}</span>
                  <span className="mono" style={{ textAlign:"right", fontSize:11.5, color:"var(--term-ink-1)" }}>{fmtAbbr(y.principal_usd)}</span>
                  <span className="mono" style={{ textAlign:"right", fontSize:11, color:"var(--term-green)" }}>+${Number(y.earned_30d_usd).toLocaleString()}</span>
                  <span className="mono" style={{ textAlign:"right", fontSize:11.5, color:"var(--term-green)", fontWeight:500 }}>{y.apy.toFixed(2)}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — custody / chain / sweeps / FX */}
        <div className="term-pane">
          <div className="term-pane-head">
            Custody · counterparty <span className="meta">{custody.length} venues</span>
          </div>
          <div className="term-pane-body" style={{ flex:"none", maxHeight:"34%" }}>
            {custody.map((c, i) => {
              const typeC = c.type === "CRYPTO" ? "#2CCB7E" : c.type === "BANK" ? "#4A8CFF" : c.type === "STAKING" ? "#A78BFA" : "#F5A524";
              return (
                <div key={i} className="term-tx-row" style={{ gridTemplateColumns:"1fr 60px 80px" }}>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:11.5, color:"var(--term-ink-1)" }}>{c.venue}</div>
                    <div className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.policy}</div>
                  </div>
                  <span style={{ fontSize:9, letterSpacing:"0.10em", fontWeight:600, color:typeC }}>{c.type}</span>
                  <span className="mono" style={{ textAlign:"right", fontSize:11, color:"var(--term-ink-1)" }}>{fmtAbbr(c.value_usd)}</span>
                </div>
              );
            })}
          </div>

          <div className="term-pane-head term-pane-split">
            Chain exposure <span className="meta">{chains.length} networks</span>
          </div>
          <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--term-line)" }}>
            <div style={{ display:"flex", height:10, border:"1px solid var(--term-line)", overflow:"hidden" }}>
              {chains.map((c, i) => (
                <div key={i} title={`${c.chain} · ${fmtAbbr(c.value_usd)}`} style={{ flex: parseFloat(c.value_usd), background: c.color_hint }} />
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 14px", marginTop:8 }}>
              {chains.map((c, i) => (
                <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:10.5 }}>
                  <span style={{ width:8, height:8, background:c.color_hint }} />
                  <span style={{ color:"var(--term-ink-2)" }}>{c.chain}</span>
                  <span className="mono" style={{ color:"var(--term-ink-3)", marginLeft:"auto" }}>{c.share_pct.toFixed(1)}%</span>
                </span>
              ))}
            </div>
          </div>

          <div className="term-pane-head term-pane-split">
            Sweep rules · auto <span className="meta">{sweepRules.filter((r) => r.active).length}/{sweepRules.length} on</span>
          </div>
          <div className="term-pane-body" style={{ flex:"none", maxHeight:"30%" }}>
            {sweepRules.map((r, i) => (
              <div key={i} style={{ padding:"10px 14px", borderBottom:"1px solid var(--term-line-dim)" }}>
                <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8 }}>
                  <span style={{ fontSize:11.5, color: r.active ? "var(--term-ink-1)" : "var(--term-ink-3)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.label}</span>
                  <span style={{ fontSize:9, letterSpacing:"0.12em", color: r.active ? "var(--term-green)" : "var(--term-ink-3)", fontWeight:600, flexShrink:0 }}>{r.active ? "● ON" : "○ OFF"}</span>
                </div>
                <div className="mono" style={{ display:"flex", justifyContent:"space-between", marginTop:3, fontSize:9.5, color:"var(--term-ink-3)" }}>
                  <span>{r.id} · {r.trigger_kind}</span>
                  <span>{r.last_run_iso}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="term-pane-head term-pane-split">
            FX exposure <span className="meta">net unhedged</span>
          </div>
          <div style={{ padding:"10px 14px" }}>
            {fxExposure.map((f, i) => (
              <div key={i} className="mono" style={{ display:"grid", gridTemplateColumns:"40px 1fr 70px", gap:10, alignItems:"center", padding:"4px 0", fontSize:10.5 }}>
                <span style={{ color:"var(--term-ink-2)" }}>{f.currency}</span>
                <span style={{ position:"relative", height:6, background:"var(--term-line-dim)" }}>
                  <span style={{ position:"absolute", inset:0, width:`${f.hedge_pct}%`, background:"#2CCB7E", opacity:0.7 }} />
                  <span style={{ position:"absolute", left:`${f.hedge_pct}%`, right:0, top:0, bottom:0, background:"#F5A524", opacity:0.5 }} />
                </span>
                <span style={{ textAlign:"right", color: f.hedge_pct === 0 ? "var(--term-amber)" : "var(--term-ink-1)" }}>{f.hedge_pct}% hdg</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
